/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 */

import type { ThreadCluster, ThreadConversation } from '@repo/common';
import { getTimeRange } from '@repo/toolslib/src/utils/date.util';
import type { Readable } from 'svelte/store';
import { derived, get, readable } from 'svelte/store';
import { getThreads } from '../data/api.memory';
import type { DataModel } from '../utils/store.util';
import { createStoreHandle } from '../utils/store.util';
import { userAuthStore } from './auth.store';

const FILENAME = '.threads.dat';
const UPDATE_FREQUENCY = 5 * 60 * 1000; // 5 minutes in milliseconds

interface ConversationThreads {
  list: ThreadConversation[];
  transcriptIdMap: Record<string, ThreadConversation>;
  conversationIdMap: Record<string, ThreadConversation>;
}

const emptyThreads: ConversationThreads = {
  list: [],
  transcriptIdMap: {},
  conversationIdMap: {}
};
// Create the calendar store handle
const {
  store: threadsModelStore,
  itemStore: threadsStore,
  setItem: setThreads,
  resetItem: resetThreads,
  loadStoredItem: loadStoredThreads,
  updateItem: _refreshThreads,
  updateOutdatedItem: updateOutdatedThreads
} = createStoreHandle<ConversationThreads>(
  FILENAME,
  emptyThreads,
  {
    updateTimeout: 5 * 60 * 1000, // 5 minutes
    retryTimeout: 5 * 1000 // 5 seconds
  },
  async (): Promise<Omit<DataModel<ConversationThreads>, 'status' | 'lastUpdated'> | undefined> => {
    let threadClusters: ThreadCluster[] = [];

    try {
      threadClusters = await getThreads(get(userAuthStore));
    } catch (error) {
      console.debug('Error getting threads', error);
      return undefined;
    }
    if (!threadClusters || typeof threadClusters.map !== 'function') {
      console.debug('No threads found');
      return undefined;
    }
    const threads = threadClusters.map(
      (cluster) =>
        ({
          type: 'thread',
          conversationId: cluster.id,
          messageCount: cluster.nodes.length,
          nodes: cluster.nodes,
          subject: cluster.subject,
          summary: cluster.summary,
          date: cluster.date || new Date(),
          isFavorite: cluster.isFavorite
        }) as ThreadConversation
    );

    console.log('Received threads', threads.length);

    const conversationIdMap = threads.reduce(
      (acc, thread) => {
        acc[thread.conversationId] = thread;
        return acc;
      },
      {} as Record<string, ThreadConversation>
    );

    return {
      item: {
        list: threads,
        transcriptIdMap: {},
        conversationIdMap: conversationIdMap
      },
      nextUpdate: new Date(Date.now() + UPDATE_FREQUENCY).getTime(),
      needsPatch: false
    };
  }
);

const refreshThreads = async () => {
  console.log('Refreshing threads');
  return await _refreshThreads();
};

// Force retry threads even when in error state
export const forceRetryThreads = async () => {
  const $model = get(threadsModelStore);

  // If in error state or failed to load, force a refresh
  if ($model.status === 'error' || $model.item.list.length === 0) {
    console.log('Forcing threads retry due to error state or empty list');
    await refreshThreads();
  } else {
    // Otherwise use the normal update logic
    await updateOutdatedThreads();
  }
};

// Utility to check if threads need a retry (for UI indicators)
export const threadsNeedRetry = derived(
  threadsModelStore,
  ($model) =>
    $model.status === 'error' || ($model.status === 'loaded' && $model.item.list.length === 0)
);

export const getThreadByConversationId = (id: string): ThreadConversation | undefined => {
  return get(threadsStore).conversationIdMap[id];
};

export {
  loadStoredThreads,
  refreshThreads,
  resetThreads,
  setThreads,
  threadsModelStore,
  threadsStore,
  updateOutdatedThreads
};

type FilterThreadFn<T extends ThreadConversation> = (a: T) => boolean;

export const filterThreads = <T extends ThreadConversation>(
  fn: FilterThreadFn<T>
): Readable<T[]> => {
  return derived(threadsStore, ($t): T[] => {
    return [...($t.list as T[])]
      .filter((thread) => {
        return fn(thread);
      })
      .sort((t1, t2) => {
        const d1 = new Date(t1.date || 0);
        const d2 = new Date(t2.date || 0);
        // @ts-expect-error These will always be dates
        return d2 - d1;
      });
  });
};

export const threadsByTimeGrain = (
  timeGrain: 'day' | 'week' | 'month' | 'all' | undefined,
  i = 0
): Readable<ThreadConversation[]> => {
  if (!timeGrain) {
    return readable([]);
  }

  const range = getTimeRange(timeGrain, i);
  if (!range) {
    return filterThreads<ThreadConversation>(($t) => $t.type === 'thread' && !!$t.date);
  }
  const [startRange, endRange] = range;
  console.log(`Threads ${timeGrain} ${i}`, startRange, endRange);

  return filterThreads<ThreadConversation>(($t) => {
    const d = new Date($t.date || 0);
    return d > startRange && d < endRange && $t.type === 'thread' && !!$t.date;
  });
};
